import type { ApiKeyView } from "@mailhand/core/api";
import { apiKeyCreateSchema } from "@mailhand/core/schemas";
import { type SubmitEvent, useState } from "react";
import { useApi } from "../../app/services.js";
import { useTask } from "../../app/useTask.js";
import { shortTime } from "../../lib/format.js";
import { useResource } from "../../lib/useResource.js";
import { Button, Card, CardHeader, CopyButton, ErrorNote, Field, Input, Spinner } from "../../ui/index.js";

export function KeysSection() {
  const api = useApi();
  const task = useTask();
  const { resource, reload } = useResource<ApiKeyView[]>(async () => (await api.call("keysList", {})).keys, [api]);
  const [label, setLabel] = useState("");
  const [error, setError] = useState<string | undefined>(undefined);
  const [created, setCreated] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const now = new Date();

  async function create(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    const parsed = apiKeyCreateSchema.safeParse({ label });
    if (!parsed.success) {
      setError("Give the key a label, like BotBuilt.");
      return;
    }
    setError(undefined);
    await task.run(async () => {
      const { key } = await api.call("keyCreate", { body: parsed.data });
      setCreated(key);
      setLabel("");
      await reload();
    });
  }

  async function remove(id: string): Promise<void> {
    await task.run(async () => {
      await api.call("keyDelete", { params: { id } });
      setDeleting(null);
      await reload();
    }, "Key deleted. Scripts that use it stop working now.");
  }

  return (
    <Card id="keys">
      <CardHeader eyebrow="API keys" title="For your scripts" />
      {created === null ? null : (
        <div className="secret-once" role="status">
          <p className="small">Copy this key now. It is not shown again.</p>
          <div className="address-line">
            <span className="mono address">{created}</span>
            <CopyButton value={created} />
          </div>
          <Button size="sm" variant="ghost" onClick={() => { setCreated(null); }}>
            I saved it
          </Button>
        </div>
      )}
      {resource.status === "loading" ? <Spinner /> : null}
      {resource.status === "error" ? <ErrorNote message={resource.message} onRetry={() => void reload()} /> : null}
      {resource.status === "ready" && resource.data.length > 0 ? (
        <ul className="rows">
          {resource.data.map((key) => (
            <li key={key.id} className="row">
              <span className="row-main">
                <span>{key.label}</span>
                <span className="mono muted small">
                  made {shortTime(key.createdAt, now)} · {key.lastUsedAt === undefined ? "never used" : `used ${shortTime(key.lastUsedAt, now)}`}
                </span>
              </span>
              {deleting === key.id ? (
                <span className="button-row">
                  <Button size="sm" variant="danger" busy={task.busy} onClick={() => void remove(key.id)}>
                    Yes, delete
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setDeleting(null); }}>
                    Keep
                  </Button>
                </span>
              ) : (
                <Button size="sm" variant="ghost" onClick={() => { setDeleting(key.id); }}>
                  Delete
                </Button>
              )}
            </li>
          ))}
        </ul>
      ) : null}
      <form className="inline-form" onSubmit={(event) => void create(event)} noValidate>
        <Field label="New key label" error={error}>
          {({ id, describedBy, invalid }) => (
            <Input id={id} value={label} maxLength={60} placeholder="BotBuilt" aria-describedby={describedBy} aria-invalid={invalid} onChange={(event) => { setLabel(event.target.value); }} />
          )}
        </Field>
        <Button type="submit" busy={task.busy}>
          Create key
        </Button>
      </form>
    </Card>
  );
}
